import React, { useState } from "react";
import axios from "axios";
import { UserPlus, Loader2, Save, Phone, MapPin, User as UserIcon, CheckCircle2, DollarSign } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "إضافة زبون جديد" (admin-only). Per Feb 2026 policy, only the admin can
// create customer accounts. The supervisor's app loses this ability and
// just picks from the existing customer list. Customers added here become
// available immediately for: direct sale, supervisor sale, account balances,
// customer profits, debt audit — everywhere.
export default function AddCustomerTab() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [market, setMarket] = useState("");
  const [location, setLocation] = useState("");
  const [tier, setTier] = useState("A_plus");
  // 💰 مديونية ابتدائية — يُرصد كـbalance_adjustment عند الحفظ لو > 0
  const [openingDebt, setOpeningDebt] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null); // last successfully added — small "history"

  const reset = () => { setName(""); setPhone(""); setMarket(""); setLocation(""); setTier("A_plus"); setOpeningDebt(""); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error("اسم الزبون مطلوب"); return; }
    if (!phone.trim() || phone.trim().length < 7) { toast.error("رقم الهاتف غير صحيح"); return; }
    setBusy(true);
    try {
      const debtValue = Number(openingDebt.toString().replace(/[^\d.]/g, "")) || 0;
      const { data } = await axios.post(`${API}/admin/customers`, {
        full_name: name.trim(),
        phone: phone.trim(),
        market_name: market.trim(),
        location: location.trim(),
        customer_tier: tier,
        opening_debt: debtValue > 0 ? debtValue : undefined,
      });
      if (data.duplicate) {
        toast.warning("هذا الرقم مسجّل مسبقاً — تم اختيار الحساب القائم");
      } else if (debtValue > 0) {
        toast.success(`تمّت إضافة الزبون + تسجيل مديونية ابتدائية ${debtValue.toLocaleString()} د.ع`);
      } else {
        toast.success("تمّت إضافة الزبون بنجاح");
      }
      setLast({
        name: data.customer.full_name || name,
        phone: data.customer.phone || phone,
        market: data.customer.market_name || market,
        openingDebt: !data.duplicate ? debtValue : 0,
        duplicate: !!data.duplicate,
        at: new Date(),
      });
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر إضافة الزبون");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl mx-auto space-y-4" dir="rtl" data-testid="add-customer-tab">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-bl from-[#1E3A5F] via-[#2c5282] to-[#7c3aed] text-white p-5 shadow-2xl">
        <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#FBBF24]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <UserPlus className="w-7 h-7" />
          </div>
          <div>
            <h2 className="font-black text-xl leading-tight">إضافة زبون جديد</h2>
            <p className="text-[11px] opacity-80 leading-snug">
              صلاحية الأدمن فقط. الزبون يصير متاح فوراً لجميع المسارات:
              البيع المباشر، تطبيق المشرف، أرصدة الحسابات، الفواتير...
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="bg-white rounded-2xl border-2 border-[#E4EBE6] p-4 space-y-3" data-testid="add-customer-form">
        <div>
          <label className="text-xs font-bold text-[#1E3A5F] flex items-center gap-1 mb-1">
            <UserIcon className="w-3.5 h-3.5" /> اسم الزبون <span className="text-[#DC2626]">*</span>
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="مثلاً: ماركت ابو علي الكفيل"
            data-testid="ac-name"
            disabled={busy}
            className="w-full px-3 py-2.5 rounded-xl border-2 border-[#3B82F6]/40 focus:border-[#3B82F6] focus:outline-none text-sm disabled:bg-[#F3F6F4]"
            autoFocus
          />
        </div>

        <div>
          <label className="text-xs font-bold text-[#1E3A5F] flex items-center gap-1 mb-1">
            <Phone className="w-3.5 h-3.5" /> رقم الهاتف <span className="text-[#DC2626]">*</span>
          </label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="07XXXXXXXXX"
            dir="ltr"
            data-testid="ac-phone"
            disabled={busy}
            className="w-full px-3 py-2.5 rounded-xl border-2 border-[#3B82F6]/40 focus:border-[#3B82F6] focus:outline-none text-sm disabled:bg-[#F3F6F4]"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-[#1E3A5F] flex items-center gap-1 mb-1">
            🏪 اسم الماركت <span className="text-[10px] font-normal text-[#5C6E64]">(اختياري)</span>
          </label>
          <input
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            placeholder="مثلاً: ماركت الكفيل"
            data-testid="ac-market"
            disabled={busy}
            className="w-full px-3 py-2.5 rounded-xl border border-[#E4EBE6] focus:border-[#3B82F6] focus:outline-none text-sm disabled:bg-[#F3F6F4]"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-[#1E3A5F] flex items-center gap-1 mb-1">
            <MapPin className="w-3.5 h-3.5" /> العنوان <span className="text-[10px] font-normal text-[#5C6E64]">(اختياري)</span>
          </label>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="منطقة، شارع، علامة..."
            data-testid="ac-location"
            disabled={busy}
            className="w-full px-3 py-2.5 rounded-xl border border-[#E4EBE6] focus:border-[#3B82F6] focus:outline-none text-sm disabled:bg-[#F3F6F4]"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">فئة السعر</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTier("A_plus")}
              data-testid="ac-tier-A"
              className={`py-2 rounded-xl border-2 font-bold text-sm transition-all ${
                tier === "A_plus" ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white text-[#1E3A5F] border-[#E4EBE6]"
              }`}
            >
              A+ (سعر التجزئة)
            </button>
            <button
              type="button"
              onClick={() => setTier("B_plus")}
              data-testid="ac-tier-B"
              className={`py-2 rounded-xl border-2 font-bold text-sm transition-all ${
                tier === "B_plus" ? "bg-[#15803d] text-white border-[#15803d]" : "bg-white text-[#1E3A5F] border-[#E4EBE6]"
              }`}
            >
              B+ (سعر الجملة)
            </button>
          </div>
        </div>

        {/* 💰 مديونية الزبون الابتدائية — اختياري. تُرصد كسطر في كشف الحساب
             تلقائياً باسم "opening_balance" ويظهر الرصيد بلوحة الديون فوراً. */}
        <div>
          <label className="text-xs font-bold text-[#1E3A5F] flex items-center gap-1 mb-1">
            <DollarSign className="w-3.5 h-3.5 text-[#DC2626]" />
            المديونية الابتدائية
            <span className="text-[10px] font-normal text-[#5C6E64]">(اختياري — لو الزبون عليه دين قديم)</span>
          </label>
          <div className="relative">
            <input
              value={openingDebt}
              onChange={(e) => setOpeningDebt(e.target.value.replace(/[^\d.,]/g, ""))}
              placeholder="مثلاً: 1,000,000"
              dir="ltr"
              data-testid="ac-opening-debt"
              disabled={busy}
              inputMode="decimal"
              className="w-full px-3 py-2.5 pr-16 rounded-xl border-2 border-[#DC2626]/40 focus:border-[#DC2626] focus:outline-none text-sm disabled:bg-[#F3F6F4] font-bold text-[#DC2626]"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#5C6E64] pointer-events-none">
              د.ع
            </span>
          </div>
          {openingDebt && Number(openingDebt.replace(/[^\d.]/g, "")) > 0 && (
            <p className="text-[11px] text-[#DC2626] mt-1 font-bold">
              💰 سيبدأ الزبون بمديونية {Number(openingDebt.replace(/[^\d.]/g, "")).toLocaleString()} د.ع بكشف حسابه
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="ac-submit"
          className="w-full py-3 rounded-2xl text-white font-black text-base shadow-lg disabled:opacity-60 flex items-center justify-center gap-2 bg-gradient-to-l from-[#15803d] to-[#0f6630] hover:from-[#0f6630]"
        >
          {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> جاري الحفظ...</> : <><Save className="w-5 h-5" /> حفظ الزبون</>}
        </button>
      </form>

      {/* Last-added confirmation */}
      {last && (
        <div className="bg-[#ECFDF5] border-2 border-[#15803d]/30 rounded-2xl p-3 flex items-start gap-2" data-testid="ac-last-added">
          <CheckCircle2 className="w-5 h-5 text-[#15803d] shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-black text-[#15803d]">
              {last.duplicate ? "تم استخدام حساب موجود" : "تمّت الإضافة"}: {last.name}
            </p>
            <p className="text-[#5C6E64] mt-0.5" dir="ltr">{last.phone}{last.market ? ` · ${last.market}` : ""}</p>
            {last.openingDebt > 0 && (
              <p className="text-[#DC2626] mt-1 font-bold text-xs" dir="ltr">
                💰 مديونية ابتدائية: {last.openingDebt.toLocaleString()} د.ع
              </p>
            )}
            <p className="text-[10px] text-[#5C6E64] mt-1">
              الزبون متاح الآن في كل القوائم. شغّل البيع المباشر أو تطبيق المشرف لتجده.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
